// src/supabaseClient.js
import { createClient } from '@supabase/supabase-js';

const SUPABASE_URL  = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON = process.env.REACT_APP_SUPABASE_ANON_KEY;

export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON);

const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// ─── AUTH ─────────────────────────────────────────────────
export async function signUp(email, password, name, role) {
  const { data, error } = await supabase.auth.signUp({
    email, password, options: { data: { name, role } }
  });
  if (error) throw error;
  return data.user;
}

export async function signIn(email, password) {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw error;
  const { data: profile } = await supabase
    .from('profiles').select('*').eq('id', data.user.id).single();
  return { ...data.user, ...profile };
}

export async function signOut() {
  await supabase.auth.signOut();
}

export async function getSession() {
  const { data } = await supabase.auth.getSession();
  if (!data.session) return null;
  const { data: profile } = await supabase
    .from('profiles').select('*').eq('id', data.session.user.id).single();
  if (!profile) return null;
  return { ...data.session.user, ...profile };
}

export async function updateProfile(userId, updates) {
  const { error } = await supabase.from('profiles').update(updates).eq('id', userId);
  if (error) throw error;
}

// ─── TEAMS ────────────────────────────────────────────────
export async function fetchTeams() {
  const { data, error } = await supabase
    .from('teams').select(`*, team_members(*)`).order('created_at');
  if (error) throw error;
  return data.map(t => ({
    id: t.id, name: t.name, color: t.color,
    members: (t.team_members || []).map(m => ({
      id: m.id, name: m.name, role: m.role,
      shift: m.shift, shiftStart: m.shift_start, shiftEnd: m.shift_end,
      days: m.days || [], status: m.status, leaveUntil: m.leave_until || '',
    }))
  }));
}

export async function createTeam(name, color, createdBy) {
  const { data, error } = await supabase
    .from('teams').insert({ name, color, created_by: createdBy }).select().single();
  if (error) throw error;
  return { id: data.id, name: data.name, color: data.color, members: [] };
}

export async function updateTeam(team) {
  await supabase.from('teams')
    .update({ name: team.name, color: team.color }).eq('id', team.id);

  const members = team.members || [];
  const existingMembers = members.filter(m => uuidRegex.test(m.id));
  const newMembers      = members.filter(m => !uuidRegex.test(m.id));

  for (const m of existingMembers) {
    await supabase.from('team_members').update({
      name: m.name, role: m.role,
      shift: m.shift, shift_start: m.shiftStart, shift_end: m.shiftEnd,
      days: m.days, status: m.status,
      leave_until: m.leaveUntil || null,
    }).eq('id', m.id);
  }

  if (newMembers.length) {
    await supabase.from('team_members').insert(
      newMembers.map(m => ({
        team_id: team.id,
        name: m.name, role: m.role,
        shift: m.shift || 'flexible',
        shift_start: m.shiftStart || '09:00',
        shift_end: m.shiftEnd || '17:00',
        days: m.days || ['Mon','Tue','Wed','Thu','Fri'],
        status: m.status || 'present',
        leave_until: m.leaveUntil || null,
      }))
    );
  }

  const { data: dbMembers } = await supabase
    .from('team_members').select('id').eq('team_id', team.id);
  const incomingUUIDs = new Set(existingMembers.map(m => m.id));
  const toDelete = (dbMembers || []).filter(m => !incomingUUIDs.has(m.id) && newMembers.length === 0);
  if (toDelete.length && newMembers.length === 0) {
    await supabase.from('team_members').delete()
      .in('id', toDelete.map(m => m.id));
  }
}

export async function deleteTeam(teamId) {
  const { error } = await supabase.from('teams').delete().eq('id', teamId);
  if (error) throw error;
}

// ─── TASKS ────────────────────────────────────────────────
export async function fetchTasks() {
  const { data, error } = await supabase
    .from('tasks').select(`*, task_comments(*)`).order('created_at');
  if (error) throw error;
  return data.map(t => ({
    id: t.id, title: t.title, description: t.description,
    team: t.team_id, priority: t.priority, status: t.status,
    dueDate: t.due_date || '', links: t.links || [],
    assignees: t.assignees || [],
    comments: (t.task_comments || []).map(c => ({
      id: c.id, author: c.author, text: c.text, tag: c.tag, at: c.created_at
    })).sort((a,b) => new Date(a.at) - new Date(b.at)),
    history: [],
  }));
}

export async function createTask(task, createdBy) {
  const { data, error } = await supabase.from('tasks').insert({
    title: task.title, description: task.description || '',
    team_id: task.team || null, priority: task.priority || 'Medium',
    status: task.status || 'To Do',
    due_date: task.dueDate || null, links: task.links || [],
    assignees: task.assignees || [], created_by: createdBy,
  }).select().single();
  if (error) throw error;
  return { ...task, id: data.id, comments: [], history: [] };
}

export async function updateTask(task) {
  const { error } = await supabase.from('tasks').update({
    title: task.title, description: task.description,
    team_id: task.team, priority: task.priority, status: task.status,
    due_date: task.dueDate || null, links: task.links || [],
    assignees: task.assignees || [],
  }).eq('id', task.id);
  if (error) throw error;
}

export async function deleteTask(taskId) {
  const { error } = await supabase.from('tasks').delete().eq('id', taskId);
  if (error) throw error;
}

export async function addComment(taskId, author, text, tag) {
  const { data, error } = await supabase.from('task_comments')
    .insert({ task_id: taskId, author, text, tag }).select().single();
  if (error) throw error;
  return { id: data.id, author, text, tag, at: data.created_at };
}

// ─── NOTIFICATIONS ────────────────────────────────────────
export async function fetchNotifications() {
  const { data, error } = await supabase
    .from('notifications').select('*').order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []).map(n => ({
    id: n.id, userId: n.user_id, teamId: n.team_id,
    type: n.type, taskTitle: n.task_title,
    from: n.from_user, read: n.read, at: n.created_at,
  }));
}

export async function insertNotification(userId, type, taskTitle, fromUser, teamId) {
  await supabase.from('notifications').insert({
    user_id: userId, type, task_title: taskTitle,
    from_user: fromUser || 'System', team_id: teamId || null,
  });
}

export async function markNotificationRead(id) {
  await supabase.from('notifications').update({ read: true }).eq('id', id);
}

export async function markAllNotificationsRead(userId) {
  await supabase.from('notifications').update({ read: true }).eq('user_id', userId);
}

// ─── ACTIVITY ─────────────────────────────────────────────
export async function fetchActivity() {
  const { data, error } = await supabase
    .from('activity').select('*').order('created_at', { ascending: false }).limit(50);
  if (error) throw error;
  return (data || []).map(a => ({
    id: a.id, user: a.user_name, teamId: a.team_id,
    action: a.action, taskTitle: a.task_title, at: a.created_at,
  }));
}

export async function insertActivity(userName, action, taskTitle, teamId) {
  await supabase.from('activity').insert({
    user_name: userName, action, task_title: taskTitle, team_id: teamId || null,
  });
}

// ─── MEETINGS ─────────────────────────────────────────────
export async function fetchMeetings() {
  const { data, error } = await supabase
    .from('meetings')
    .select('*')
    .order('date', { ascending: true });
  if (error) { console.error('fetchMeetings:', error); return []; }
  return (data || []).map(m => ({
    id:        m.id,
    title:     m.title,
    date:      m.date,
    startTime: m.start_time,
    endTime:   m.end_time,
    notes:     m.notes,
    color:     m.color,
    teamId:    m.team_id,
    teamIds:   m.team_ids || [],
    invitees:  m.invitees || [],
    createdBy: m.created_by,
  }));
}

export async function upsertMeeting(meeting) {
  const { error } = await supabase
    .from('meetings')
    .upsert({
      id:         meeting.id,
      title:      meeting.title,
      date:       meeting.date,
      start_time: meeting.startTime,
      end_time:   meeting.endTime,
      notes:      meeting.notes || '',
      color:      meeting.color || '#0065FF',
      team_id:    meeting.teamId || null,
      team_ids:   meeting.teamIds || [],
      invitees:   meeting.invitees || [],
      created_by: meeting.createdBy || '',
    }, { onConflict: 'id' });
  if (error) console.error('upsertMeeting:', error);
}

export async function deleteMeetingById(id) {
  const { error } = await supabase
    .from('meetings')
    .delete()
    .eq('id', id);
  if (error) console.error('deleteMeetingById:', error);
}

// ─── RENAME USER ACROSS ALL DATA ─────────────────────────
export async function renameUserEverywhere(oldName, newName) {
  await supabase.from('team_members').update({ name: newName }).eq('name', oldName);
  const { data: tasks } = await supabase.from('tasks')
    .select('id, assignees').contains('assignees', [oldName]);
  for (const t of (tasks || [])) {
    await supabase.from('tasks').update({
      assignees: t.assignees.map(a => a === oldName ? newName : a)
    }).eq('id', t.id);
  }
  await supabase.from('task_comments').update({ author: newName }).eq('author', oldName);
  await supabase.from('notifications').update({ user_id: newName }).eq('user_id', oldName);
  await supabase.from('notifications').update({ from_user: newName }).eq('from_user', oldName);
  await supabase.from('activity').update({ user_name: newName }).eq('user_name', oldName);
  await supabase.from('meetings').update({ created_by: newName }).eq('created_by', oldName);
}
